import { useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import ConfirmModal from "./ConfirmModal.jsx";
import HoverTextField from "./HoverTextField.jsx";
import useRakennuslupaData from "./hooks/useRakennuslupaData.js";

// Self-contained, live-DB-backed component — same family as CapexYhteenveto.jsx
// (reads its own data via a hook keyed on the route's kiinteistö id, no
// { data, onChange } props from a caller). Used identically in both places
// it appears:
//   1. The Taloyhtiökortti "Järjestelmäkuvaus" tab (pages/Taloyhtiokortti.js),
//      passed `editable` — the only place edits happen.
//   2. A report block (blockUtils.js's registry) — `editable` omitted, so it
//      renders read-only. Previously this stored its own frozen snapshot in
//      the report's JSON content (report_sections.content), which could
//      drift from the live kiinteistö data; now both places always show the
//      exact same thing.
// Template-editing view (no kiinteistoId in the route) has no live data
// context to read, so this renders nothing there.

const newRow = (overrides = {}) => ({
  id: crypto.randomUUID(),
  lupatunnus: "",
  tyyppi: "",
  lahde: "",
  myonnetty: "",
  huomiot: "",
  ...overrides,
});

const ROW_FIELDS = ["lupatunnus", "tyyppi", "lahde", "myonnetty", "huomiot"];

const SHARED_STYLE = `
  .lupa-card-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 10px; margin-top: 8px; }
  .lupa-card { border-radius: 10px; padding: 12px; background: #fafafa; }
  .lupa-card-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 14px; font-size: 0.78rem; }
  .lupa-field-label { font-size: 0.62rem; color: #8e8e93; margin-bottom: 1px; }
  .lupa-citation { font-size: 0.68rem; color: #8e8e93; }
  .lupa-table th { font-size: 0.7rem; color: #495057; white-space: nowrap; vertical-align: bottom; padding: 6px 10px; }
  .lupa-table td { vertical-align: top; font-size: 0.78rem; padding: 6px 10px; }
`;

// Identical in every usage — the Taloyhtiökortti tab and a report block
// both get the exact same full editing UI, with no mode distinction
// between them (there was one; it's gone, per explicit instruction).
export default function RakennuslupaTaulukko() {
  const { id: kiinteistoId } = useParams();
  const rl = useRakennuslupaData(kiinteistoId);

  // Template-editing view (no kiinteistö in the route) has no live data to
  // show at all — same as CapexYhteenveto.jsx's own early-return.
  if (!kiinteistoId) return null;
  if (rl.loading) return <div className="text-center text-muted py-4">Ladataan…</div>;

  return (
    <>
      <style>{SHARED_STYLE}</style>
      {(rl.saving || rl.savedFlash || rl.error) && (
        <div className="d-flex justify-content-center gap-2 mb-2">
          {rl.saving && <span className="text-muted small">Tallennetaan…</span>}
          {!rl.saving && rl.savedFlash && <span className="small" style={{ color: "#198754" }}>Tallennettu <i className="bi bi-check-lg" /></span>}
          {rl.error && <span className="text-danger small">{rl.error}</span>}
        </div>
      )}
      <RakennuslupaTaulukkoView
        rows={rl.data?.rows || []}
        readOnly={false}
        onRowAdd={rl.onRowAdd}
        onRowFieldChange={rl.onRowFieldChange}
        onRowDelete={rl.onRowDelete}
      />
    </>
  );
}

function RakennuslupaTaulukkoView({ rows: initialRows, readOnly, onRowAdd, onRowFieldChange, onRowDelete }) {
  const [rows, setRows] = useState(initialRows);
  const [confirmRowId, setConfirmRowId] = useState(null);
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'cards'

  const lastCommittedRef = useRef(rows);

  const commit = useCallback((nextRows) => {
    const next = nextRows ?? rows;
    const prev = lastCommittedRef.current;
    if (onRowFieldChange) {
      const prevById = new Map(prev.map(r => [r.id, r]));
      for (const r of next) {
        const prevR = prevById.get(r.id);
        if (!prevR) continue; // newly added row — onRowAdd already covered it
        for (const field of ROW_FIELDS) {
          if (prevR[field] !== r[field]) onRowFieldChange(r.id, field, r[field]);
        }
      }
    }
    lastCommittedRef.current = next;
  }, [rows, onRowFieldChange]);

  const handleBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) commit(rows);
  };

  const updateRow = (rowId, field, value) => {
    const next = rows.map(r => r.id === rowId ? { ...r, [field]: value } : r);
    setRows(next);
  };

  const addRow = () => {
    const row = newRow();
    const next = [...rows, row];
    setRows(next);
    lastCommittedRef.current = next;
    onRowAdd?.(row);
  };

  const confirmRemove = () => {
    const next = rows.filter(r => r.id !== confirmRowId);
    setRows(next);
    lastCommittedRef.current = next;
    onRowDelete?.(confirmRowId);
    setConfirmRowId(null);
  };

  return (
    <>
      <div className="border rounded bg-white p-2" onBlur={handleBlur}>
        <div className="d-flex justify-content-center mb-2">
          <div className="btn-group btn-group-sm" role="group">
            <button type="button" className={`btn ${viewMode === "table" ? "btn-success" : "btn-outline-success"}`}
              onClick={() => setViewMode("table")}>
              Taulukko
            </button>
            <button type="button" className={`btn ${viewMode === "cards" ? "btn-success" : "btn-outline-success"}`}
              onClick={() => setViewMode("cards")}>
              Kortit
            </button>
          </div>
        </div>

        {viewMode === "table" && (
          <div className="table-responsive">
            <table className="table table-sm lupa-table mb-2" style={{ minWidth: 700 }}>
              <thead>
                <tr>
                  <th style={{ minWidth: 150 }}>Lupatunnus</th>
                  <th style={{ minWidth: 220 }}>Tyyppi / kuvaus</th>
                  <th style={{ minWidth: 110 }}>Myönnetty</th>
                  <th style={{ minWidth: 160 }}>Huomiot</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id}>
                    <td>
                      <HoverTextField value={row.lupatunnus} bold readOnly={readOnly}
                        onChange={val => updateRow(row.id, "lupatunnus", val)} placeholder="Lupatunnus" />
                    </td>
                    <td>
                      <HoverTextField value={row.tyyppi} bold readOnly={readOnly}
                        onChange={val => updateRow(row.id, "tyyppi", val)} placeholder="Tyyppi / kuvaus" />
                      <div className="lupa-citation">
                        <HoverTextField value={row.lahde} readOnly={readOnly}
                          onChange={val => updateRow(row.id, "lahde", val)} placeholder="Lähdedokumentti" />
                      </div>
                    </td>
                    <td>
                      <HoverTextField value={row.myonnetty} readOnly={readOnly}
                        onChange={val => updateRow(row.id, "myonnetty", val)} placeholder="pvm" />
                    </td>
                    <td>
                      <HoverTextField value={row.huomiot} readOnly={readOnly}
                        onChange={val => updateRow(row.id, "huomiot", val)} placeholder="Huomiot" />
                    </td>
                    <td className="align-middle">
                      {!readOnly && (
                        <button type="button" className="btn btn-outline-danger btn-sm" style={{ padding: "2px 7px" }}
                          onClick={() => setConfirmRowId(row.id)} disabled={rows.length === 1}>
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === "cards" && (
          <div className="lupa-card-grid">
            {rows.map(row => (
              <div key={row.id} className="lupa-card">
                <div className="d-flex align-items-start justify-content-between gap-2">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <HoverTextField value={row.lupatunnus} bold readOnly={readOnly}
                      onChange={val => updateRow(row.id, "lupatunnus", val)} placeholder="Lupatunnus" />
                  </div>
                  {!readOnly && (
                    <button type="button" className="btn btn-outline-danger btn-sm" style={{ padding: "1px 7px", flexShrink: 0 }}
                      onClick={() => setConfirmRowId(row.id)} disabled={rows.length === 1}
                      aria-label="Poista rivi" title="Poista rivi">
                      ✕
                    </button>
                  )}
                </div>
                <div className="mt-1">
                  <HoverTextField value={row.tyyppi} bold readOnly={readOnly}
                    onChange={val => updateRow(row.id, "tyyppi", val)} placeholder="Tyyppi / kuvaus" />
                  <div className="lupa-citation">
                    <HoverTextField value={row.lahde} readOnly={readOnly}
                      onChange={val => updateRow(row.id, "lahde", val)} placeholder="Lähdedokumentti" />
                  </div>
                </div>
                <div className="lupa-card-fields mt-2">
                  <div>
                    <div className="lupa-field-label">Myönnetty</div>
                    <HoverTextField value={row.myonnetty} readOnly={readOnly}
                      onChange={val => updateRow(row.id, "myonnetty", val)} placeholder="pvm" />
                  </div>
                  <div>
                    <div className="lupa-field-label">Huomiot</div>
                    <HoverTextField value={row.huomiot} readOnly={readOnly}
                      onChange={val => updateRow(row.id, "huomiot", val)} placeholder="Huomiot" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!readOnly && (
          <div className="text-center mt-2">
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={addRow}>
              + Lisää rivi
            </button>
          </div>
        )}
      </div>

      <ConfirmModal
        show={confirmRowId !== null}
        onClose={() => setConfirmRowId(null)}
        onConfirm={confirmRemove}
        title="Poista rivi"
        message="Haluatko varmasti poistaa tämän rivin?"
        confirmText="Poista"
        cancelText="Peru"
      />
    </>
  );
}
